/**
 * PropSelectionView.jsx - Prop and unit selection view
 * Phase 3B Round 2 - Named export only
 * 
 * Extracted from GMRosterBuilderV2.jsx lines 475-553
 * FIXED: z-index and bottom positioning for Confirm button
 */

import React from 'react';
import { ChevronLeft, User, Shield } from 'lucide-react';
import PropSelectionCard from '../../../components/builder/PropSelectionCard';
import { POSITION_COLORS } from '../../../components/builder/RosterSlot';

export const PropSelectionView = ({
  selectedPlayer,
  selectedProp,
  selectedSide,
  unitValue,
  availableUnits,
  onSelectProp,
  onUnitsChange,
  onConfirm,
  onBack,
}) => {
  // Null safety - required by architect directive D2
  if (!selectedPlayer) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">No player selected</p>
      </div>
    );
  }

  const props = selectedPlayer.props || [];
  const posColor = POSITION_COLORS[selectedPlayer.position] || '#64748B';
  const defLabel = selectedPlayer.passDefRank >= 25 ? 'Soft' : selectedPlayer.passDefRank <= 10 ? 'Tough' : 'Mid';

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col pb-40">
      <header className="bg-white shadow-sm sticky top-0 z-10 px-3 py-2">
        <div className="flex items-center justify-between mb-2">
          <button 
            onClick={onBack} 
            className="p-1.5 text-slate-600 active:scale-90 min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <ChevronLeft size={22} />
          </button>
          <h2 className="text-base font-bold text-slate-900">Set Strategy</h2>
          <div className="w-10" />
        </div>
        
        <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-2">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center" 
            style={{ backgroundColor: posColor + '20' }}
          >
            <User size={18} style={{ color: posColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 text-sm truncate">{selectedPlayer.name}</p>
            <p className="text-xs text-slate-500">{selectedPlayer.team} vs {selectedPlayer.opponent}</p>
          </div>
          <div className="flex items-center gap-1 bg-white rounded px-2 py-1">
            <Shield size={12} className="text-slate-400" />
            <span className={`text-xs font-bold ${defLabel === 'Soft' ? 'text-indigo-600' : 'text-slate-500'}`}>
              #{selectedPlayer.passDefRank}
            </span>
          </div>
        </div>
      </header>
      
      <main className="flex-1 overflow-y-auto p-3 space-y-2">
        {props.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <p className="font-medium">No props available</p>
            <p className="text-sm">This player has no active prop lines</p>
          </div>
        ) : (
          props.map(p => (
            <PropSelectionCard 
              key={p.id} 
              prop={p} 
              isSelected={selectedProp?.id === p.id} 
              selectedSide={selectedSide === 'OVER' ? 'Above' : 'Below'}
              units={unitValue} 
              maxUnits={availableUnits} 
              onSelect={onSelectProp}
              onUnitsChange={onUnitsChange} 
            />
          ))
        )}
      </main>
      
      {/* FIXED: bottom-16 to sit above BottomNav, z-40 to be above BottomNav's z-30 */}
      <div className="fixed bottom-16 left-0 right-0 max-w-md mx-auto bg-white border-t border-slate-200 p-3 z-40">
        {selectedProp && selectedSide && (
          <div className="bg-slate-50 rounded-xl p-3 mb-2 border-2 border-dashed border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">{selectedPlayer.name}</p>
                <p className="text-sm font-bold text-slate-800">
                  {selectedSide} {selectedProp.stat} {selectedProp.line}
                </p>
              </div>
              <p className="text-xl font-black text-indigo-600 tabular-nums">${unitValue}</p>
            </div>
          </div>
        )}
        <button 
          onClick={onConfirm} 
          disabled={!selectedProp || !selectedSide}
          className="w-full bg-indigo-600 text-white font-bold py-3 min-h-[48px] rounded-xl disabled:bg-slate-300 flex items-center justify-center gap-2"
        >
          {selectedProp && selectedSide ? 'Confirm Strategy' : 'Set Strategy'}
        </button>
      </div>
    </div>
  );
};
